<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { $, defineSchema } from '@bagelink/vue'
const schema = defineSchema({
	logo: $.upload('Logo', { default: '/assets/shalva-logo.svg' }),
	logoLight: $.upload('Logo for dark backgrounds').if('background eq "brand"'),
	logoAlt: $.text('Logo description', { default: 'Shalva home' }),
	logoTo: $.text('Logo destination', { default: '/' }),
	background: $.select('Background', [
		{ label: 'Transparent', value: 'transparent' },
		{ label: 'White surface', value: 'surface' },
		{ label: 'Brand color', value: 'brand' },
		{ label: 'Image', value: 'image' },
	], { default: 'transparent' }),
	backgroundImage: $.upload('Background image').if('background eq "image"'),
	backgroundFit: $.select('Background image fit', [
		{ label: 'Cover', value: 'cover' },
		{ label: 'Contain', value: 'contain' },
		{ label: 'Fill', value: 'fill' },
	], { default: 'cover' }).if('background eq "image" and backgroundImage pr'),
	dimBackground: $.checkbox('Dim background', { default: false }).if('background eq "image" and backgroundImage pr'),
	position: $.select('Position', [
		{ label: 'Normal', value: 'normal' },
		{ label: 'Sticky', value: 'sticky' },
	], { default: 'normal' }),
	compact: $.checkbox('Compact', { default: false }),
	links: $.array('Navigation links', defineSchema({
		label: $.text('Label'),
		to: $.text('Destination'),
		primary: $.checkbox('Primary action', { default: false }),
	}), {
		allowAdd: true,
		allowDelete: true,
		allowReorder: true,
		default: [
			{ label: 'Featuring', to: '/#Featuring' },
			{ label: 'Leadership', to: '/#Leadership' },
			{ label: 'About Team Shalva', to: '/#About' },
			{ label: 'Register/Donate', to: '/register', primary: true },
		],
	}),
})
export const EventNav = defineBlock({
	name: 'EventNav',
	label: 'Navigation',
	icon: 'menu',
	thumbnail: '/block-thumbs/EventNav.svg',
	category: 'Site layout',
	order: 10,
	schema,
	global: true,
})
</script>
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useGlobalBlock } from '@bagelink/blox'
import { Btn, Dialog, Menu, pathKeyToURL } from '@bagelink/vue'
type NavEntry = { label: string; to: string; primary?: boolean }
interface NavContent {
	logo?: string
	logoLight?: string
	logoAlt?: string
	logoTo?: string
	background?: 'transparent' | 'surface' | 'brand' | 'image'
	backgroundImage?: string
	backgroundFit?: 'cover' | 'contain' | 'fill'
	dimBackground?: boolean
	position?: 'normal' | 'sticky'
	compact?: boolean
	links?: NavEntry[]
}



const navProps = defineProps<NavContent>()
const { data: remoteNav } = useGlobalBlock<NavContent>('EventNav')

function navValue<K extends keyof NavContent>(
	key: K,
	fallback: NonNullable<NavContent[K]>,
): NonNullable<NavContent[K]> {
	const remoteValue = remoteNav.value?.[key]
	if (remoteValue !== undefined && remoteValue !== null) {
		return remoteValue as NonNullable<NavContent[K]>
	}
	const propValue = navProps[key]
	if (propValue !== undefined && propValue !== null) {
		return propValue as NonNullable<NavContent[K]>
	}
	return fallback
}

const open = ref(false)
const navElement = ref<HTMLElement | null>(null)
let navResizeObserver: ResizeObserver | null = null
const navLogo = computed(() => navValue('logo', ''))
const navLogoLight = computed(() => navValue('logoLight', ''))
const navLogoAlt = computed(() => navValue('logoAlt', ''))
const navLogoTo = computed(() => navValue('logoTo', ''))
const navBackground = computed(() => navValue('background', 'transparent'))
const navBackgroundImage = computed(() => navValue('backgroundImage', ''))
const navBackgroundFit = computed(() => navValue('backgroundFit', 'cover'))
const navDimBackground = computed(() => navValue('dimBackground', false))
const navPosition = computed(() => navValue('position', 'normal'))
const navCompact = computed(() => navValue('compact', false))
const navLinks = computed(() => navValue('links', [])
	.filter(link => link.label?.trim() && link.to?.trim()))
const items = computed(() => navLinks.value.map(link => ({
	...link,
	action: () => { open.value = false },
})))
const regularItems = computed(() => items.value.filter(item => !item.primary))
const primaryItem = computed(() => navLinks.value.find(item => item.primary))
const displayedLogo = computed(() => (
	pathKeyToURL(navBackground.value === 'brand' && navLogoLight.value ? navLogoLight.value : navLogo.value)
))
const navClass = computed(() => [
	'event-nav relative flex space-between isolation-isolate txt-20 line-height-16',
	navCompact.value ? 'p-05' : 'p-1',
	navPosition.value === 'sticky' ? 'sticky top-0 z-9' : '',
	navBackground.value === 'transparent' ? 'color-white keep-white' : '',
	navBackground.value === 'surface' ? 'bg-box color-primary shadow-light' : '',
	navBackground.value === 'brand' ? 'bg-primary color-white keep-white' : '',
	navBackground.value === 'image' ? 'color-white keep-white' : '',
])
const navStyle = computed(() => navBackground.value === 'image' && navBackgroundImage.value
	? {
			backgroundImage: `${navDimBackground.value ? 'linear-gradient(#0006, #0006), ' : ''}url("${pathKeyToURL(navBackgroundImage.value) || navBackgroundImage.value}")`,
			backgroundPosition: 'center',
			backgroundRepeat: 'no-repeat',
			backgroundSize: navBackgroundFit.value === 'fill' ? '100% 100%' : navBackgroundFit.value,
		}
	: undefined)

function updateStickyOffset() {
	if (typeof document === 'undefined') return
	const offset = navPosition.value === 'sticky' ? navElement.value?.offsetHeight ?? 0 : 0
	document.documentElement.style.scrollPaddingTop = `${offset}px`
}

watch([navPosition, navCompact], async () => {
	await nextTick()
	updateStickyOffset()
})

onMounted(() => {
	updateStickyOffset()
	if (typeof ResizeObserver !== 'undefined' && navElement.value) {
		navResizeObserver = new ResizeObserver(updateStickyOffset)
		navResizeObserver.observe(navElement.value)
	}
})

onBeforeUnmount(() => {
	navResizeObserver?.disconnect()
	if (typeof document !== 'undefined') {
		document.documentElement.style.removeProperty('scroll-padding-top')
	}
})
</script>

<template>
	<header ref="navElement" :class="navClass" :style="navStyle">
		<RouterLink v-if="displayedLogo && navLogoTo" :to="navLogoTo" :aria-label="navLogoAlt" class="relative z-1 flex-shrink-0">
			<img :src="displayedLogo" :alt="navLogoAlt" class="w-150px m_w-100px h-auto">
		</RouterLink>
		<img v-else-if="displayedLogo" :src="displayedLogo" :alt="navLogoAlt" class="relative z-1 flex-shrink-0 w-150px m_w-100px h-auto">
		<nav class="event-nav-links desktop-only relative z-1 flex gap-05" aria-label="Primary">
			<Menu :items="regularItems" />
			<Btn
				v-if="primaryItem"
				:value="primaryItem.label"
				:to="primaryItem.to"
				:color="navBackground === 'brand' ? 'white' : 'primary'"
			/>
		</nav>
		<Btn
			v-if="items.length"
			class="mobile-only relative z-1 color-inherit"
			flat
			icon="menu"
			aria-label="Open navigation"
			:aria-expanded="open"
			@click="open = true"
		/>
		<Dialog v-if="items.length" v-model:open="open" title="Navigation" position="right" width="s">
			<nav aria-label="Mobile primary">
				<Menu class="event-mobile-menu w-100p m-0" :items="items" />
			</nav>
		</Dialog>
	</header>
</template>
